<template>
  <div>
    <TopBar />

    <main class="mx-auto mt-10 md:pr-0">
      <div class="flex flex-wrap items-center lg:flex-row-reverse">
        <div class="mb-10 w-full lg:mb-0">
          <VenuesStaticMap
            v-if="showStaticMap && destination.id"
            :width="$screen.name === 'sm' ? 375 : 1280"
            :height="$screen.name === 'sm' ? 256 : 214"
            :destination="destination"
            :venues="venues"
            @click="showStaticMap = false"
          />

          <div v-if="!showStaticMap" class="relative md:mx-0">
            <ClientOnly>
              <div class="pb-64">
                <VenuesMap
                  v-if="destination.id"
                  :venues="venues"
                  :venue-clicked="venueClicked"
                  :destination="destination"
                  :show="true"
                  :is-sticky-on-scroll="false"
                  :with-search-area="false"
                  hide-close
                />
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>

      <div v-if="venue1" class="mx-auto flex max-w-screen-xl flex-wrap">
        <div class="mt-20 w-full px-4 text-slate">
          <h1 class="mb-6 flex flex-col items-center justify-center rounded-md border border-lime bg-lime px-2 py-6 text-center font-semibold md:flex-row lg:py-10">
            <a href="javascript:void(0)" class="comparison-title link md:text-right" :class="{ active: venueClicked === venue1 }" @click.prevent="venueClicked = venue1">
              <span class="text-2xl lg:text-3xl xl:text-4xl">{{ venue1.name }}</span>
            </a>
            <span class="mx-6 text-lg text-gray-dark xl:text-xl">vs</span>
            <a href="javascript:void(0)" class="comparison-title link md:text-left" @click.prevent>
              <span class="text-2xl lg:text-3xl xl:text-4xl">?</span>
            </a>
          </h1>
          <p class="text-center lg:text-lg xl:text-xl">
            Select a hotel to compare with {{ venue1.name }}.
          </p>
        </div>

        <div class="mt-20 w-full md:px-4 lg:w-1/2">
          <div class="flex rounded-lg bg-white p-5 text-gray-dark shadow-card md:p-6">
            <ComparisonVenue :venue="venue1" />
          </div>
        </div>

        <div class="mt-20 w-full md:px-4 lg:w-1/2">
          <div class="block rounded-lg bg-white p-5 text-gray-dark shadow-card md:p-12">
            <div v-for="comp in comparisons" :key="comp.id">
              <NuxtLink :to="`/${comp.path}`" class="mb-2 inline-block text-lg font-bold text-blue-dark">
                {{ comp.name }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </main>

    <MainFooter />
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useAsyncData } from 'nuxt/app'
import TopBar from '~/components/layout/TopBar'
import venueTypes from '~/config/venue-types.json'
import VenuesMap from '~/components/venues/VenuesMap'
import ComparisonVenue from '~/components/comparison/ComparisonVenue'
import MainFooter from '~/components/layout/MainFooter'
import VenuesStaticMap from '~/components/venues/VenuesStaticMap'
import { useDestinationsStore } from '~/stores/destinations'
import { showError, useComparisonStore, useHead, useNuxtApp, useRoute } from '#imports'
import { callWithNuxt } from '#app'
import { useLoadDestination } from '~/composables/useLoadDestination'

export default {
  components: { VenuesStaticMap, TopBar, VenuesMap, ComparisonVenue, MainFooter },

  async setup () {
    const route = useRoute()
    const { params } = route
    const app = useNuxtApp()
    const type = params.type.replace(/s$/, '')
    const comparisonsStore = useComparisonStore()

    const venueType = venueTypes.find(v => v.permalink === type)
    if (! venueType) {
      return showError({ statusCode: 404, statusMessage: 'Venue type not found' })
    }

    await useLoadDestination()

    const path = `${params.destination}/${params.type}/${params.name}`
    const shouldLoadVenue = ! comparisonsStore.venue1 || comparisonsStore.venue1.path !== path
    if (shouldLoadVenue) {
      try {
        await callWithNuxt(app, () => comparisonsStore.loadVenue1(path))
      } catch (e) {
        if (! e.response || (e.response && e.response.status !== 404)) { app.$sentry.captureException(e) }
      }
    }
    if (! comparisonsStore.venue1.id) {
      return showError({ statusCode: 404, statusMessage: 'Venue not found' })
    }

    try {
      await callWithNuxt(app, () => useAsyncData(() => comparisonsStore.loadComparisons()))
    } catch (e) {
      if (! e.response || (e.response && e.response.status !== 404)) { app.$sentry.captureException(e) }
    }

    const description = comparisonsStore.venue1 ? `${comparisonsStore.venue1.name} vs ? | Tripexpert` : 'Comparison Tripexpert'
    callWithNuxt(app, () => useHead({
      title: comparisonsStore.venue1 ? `${comparisonsStore.venue1.name} vs ? | Tripexpert` : 'Comparison Tripexpert',
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'og:description', property: 'og:description', content: description },
      ],
    }))
  },

  data: () => {
    return {
      venueClicked: null,
      datesRange: {
        startDate: null,
        endDate: null,
      },

      numRooms: 1,
      showModal: false,
      showStaticMap: true,
    }
  },

  computed: {
    ...mapState(useDestinationsStore, {
      destination: s => s.destination.data,
    }),

    ...mapState(useComparisonStore, {
      comparisons: s => s.comparisons.data,
      venue1: s => s.venue1,
      venues: s => [s.venue1],
    }),
  },

  mounted () {
    setTimeout(() => (this.showModal = true), 10000)
  },
}
</script>
